import { useCallback, useContext, useEffect, useSyncExternalStore } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { patchUser, type User } from "../services/UserService";
import { useUserContext } from "../context/UserContext";
import {
  hiddenExtraStoreIds,
  readLocalHiddenExtraStores,
  resolveHiddenExtraStores,
  sameHiddenExtraStores,
  setExtraStoreHidden,
  writeLocalHiddenExtraStores,
  type HiddenExtraStoreMap,
  type LocalHiddenExtraStores,
} from "../utils/hiddenExtraStores";

const NO_HIDDEN_STORES: HiddenExtraStoreMap = {};

// One shared store so the products page, the store picker and Bot filters
// all see a hide at once.
type Snapshot = { userId: string | null; stores: HiddenExtraStoreMap };
let snapshot: Snapshot = { userId: null, stores: {} };
let localState: LocalHiddenExtraStores | null = null;
const listeners = new Set<() => void>();
let pushChain: Promise<void> = Promise.resolve();
let pushQueued = false;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => snapshot;

function publish(next: Snapshot) {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

function saveLocal(userId: string, value: LocalHiddenExtraStores) {
  localState = value;
  writeLocalHiddenExtraStores(userId, value);
}

/** Loads the cached value for a newly seen user without notifying (safe in render). */
function adoptUser(userId: string | null) {
  if (snapshot.userId === userId) return;
  localState = userId ? readLocalHiddenExtraStores(userId) : null;
  snapshot = { userId, stores: localState?.stores ?? {} };
}

const serverSupportsField = (user: Pick<User, "hiddenExtraStores"> | null | undefined) =>
  !!user?.hiddenExtraStores && typeof user.hiddenExtraStores === "object";

function schedulePush(userId: string, onServerUser: (user: User) => void) {
  if (pushQueued) return;
  pushQueued = true;
  pushChain = pushChain.then(async () => {
    pushQueued = false;
    if (snapshot.userId !== userId) return;
    const stores = snapshot.stores;
    try {
      const updated = await patchUser({ hiddenExtraStores: stores });
      if (snapshot.userId === userId && sameHiddenExtraStores(snapshot.stores, stores) && serverSupportsField(updated)) {
        saveLocal(userId, { stores, pending: false, synced: true });
      }
      onServerUser(updated);
    } catch {
      // Servers before the hiddenExtraStores release answer 400 "Unsupported
      // user profile field". Keep the local value; it is pushed once a server
      // returns the field.
    }
  });
}

/** Test seam: forget the shared state between tests. */
export function resetHiddenExtraStoresForTests() {
  snapshot = { userId: null, stores: {} };
  localState = null;
  pushChain = Promise.resolve();
  pushQueued = false;
}

export function useHiddenExtraStores() {
  const { user } = useUserContext();
  // Read without throwing: screens rendered outside a QueryClientProvider (tests)
  // still get the local preference.
  const queryClient = useContext(QueryClientContext);
  const userId = user?.userId ?? null;
  adoptUser(userId);
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const onServerUser = useCallback(
    (updated: User) => {
      if (!updated?.userId || !queryClient) return;
      queryClient.setQueryData<User>(["user"], (old) => (old && old.userId === updated.userId ? { ...old, ...updated } : old));
    },
    [queryClient]
  );

  // Merge the server value (when the server has the field) into the cache.
  const serverValue = user?.hiddenExtraStores;
  useEffect(() => {
    if (!userId) return;
    adoptUser(userId);
    const resolved = resolveHiddenExtraStores(serverValue, localState);
    saveLocal(userId, resolved.local);
    if (!sameHiddenExtraStores(snapshot.stores, resolved.stores)) publish({ userId, stores: resolved.stores });
    if (resolved.push) schedulePush(userId, onServerUser);
  }, [userId, serverValue, onServerUser]);

  const setHidden = useCallback(
    (retailer: string, storeId: string, hidden: boolean) => {
      if (!userId || snapshot.userId !== userId) return false;
      const next = setExtraStoreHidden(snapshot.stores, retailer, storeId, hidden);
      if (next === snapshot.stores) return false;
      publish({ userId, stores: next });
      saveLocal(userId, { stores: next, pending: true, synced: localState?.synced });
      if (serverSupportsField(user)) schedulePush(userId, onServerUser);
      return true;
    },
    [userId, user, onServerUser]
  );

  const stores = current.userId === userId ? current.stores : NO_HIDDEN_STORES;
  const idsFor = useCallback((retailer: string | null | undefined) => hiddenExtraStoreIds(stores, retailer), [stores]);
  return {
    hiddenExtraStores: stores,
    hiddenIdsFor: idsFor,
    hideExtraStore: useCallback((retailer: string, storeId: string) => setHidden(retailer, storeId, true), [setHidden]),
    showExtraStore: useCallback((retailer: string, storeId: string) => setHidden(retailer, storeId, false), [setHidden]),
  };
}
